import { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Select, MenuItem, FormControl, InputLabel, ListSubheader, Button } from '@mui/material';
import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import EditInventoryModal from './EditInventoryModal';

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]/g, '');
    if (!numericString) {
      return undefined;
    }
    const parsed = Number(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatDateValue = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const normalizeTenancyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'own') {
    return 'owned';
  }
  if (normalized === 'iru') {
    return 'leased';
  }
  return normalized;
};

const formatTenancyDisplay = (value) => {
  const normalized = normalizeTenancyValue(value);
  if (normalized === 'owned') {
    return 'Owned';
  }
  if (normalized === 'leased') {
    return 'Leased';
  }
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const formatLengthValue = (value) => {
  const numeric = parseNumber(value);
  if (numeric === undefined) {
    return '--';
  }
  return `${numeric.toFixed(2)} KM`;
};

export default function LeasedFiberTable({ data = [], onUpdateRecord, network }) {
  const [fiberTab, setFiberTab] = useState('ALL');
  const [expiryFilter, setExpiryFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const cleanedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    const normalizedData = data.map((item) => {
      const fiberPair = parseNumber(item.fiber_pair ?? item.pair_count);
      const usedPair = parseNumber(item.used_pair);
      const availablePair =
        parseNumber(item.available_pair ?? item.unused_pair) ??
        (fiberPair !== undefined && usedPair !== undefined
          ? Math.max(fiberPair - usedPair, 0)
          : undefined);

      const tenancyType = item.fiber_tenancy_type ?? item.tenancy_type ?? '';
      const normalizedTenancy = normalizeTenancyValue(tenancyType);
      const tenancyDisplay = formatTenancyDisplay(tenancyType);

      return {
        raw: item,
        normalizedTenancy,
        formatted: {
          layer_name: item.layer_name ?? '--',
          fiber_type: item.fiber_type ?? '--',
          fiber_pair: fiberPair,
          used_pair: usedPair,
          available_pair: availablePair,
          link_rate_gbps: item.link_rate_gbps ?? item.link_rate ?? '--',
          fiber_tenancy_type: tenancyDisplay,
          commission_date: formatDateValue(item.commission_date ?? item.commissioned_at),
          scope_year: item.scope_year ?? item.scopeYear ?? '--',
          region: item.region ?? item.source_region ?? '--',
          city: item.city ?? item.source_city ?? '--',
          ring_unique_name: item.ring_unique_name ?? item.source_ring ?? '--',
          source_site_code: item.source_site_code ?? '--',
          sink_site_code: item.sink_site_code ?? '--',
          link_type: item.link_type ?? '--',
          vendor: item.vendor ?? '--',
          length: item.length ?? item.link_length ?? item.link_length_km ?? item.distance_km,
        },
      };
    });

    if (fiberTab === 'MY_FIBER') {
      return normalizedData
        .filter(
          (entry) => entry.normalizedTenancy === 'owned' || entry.normalizedTenancy === 'myfiber'
        )
        .map((entry) => ({
          ...entry.formatted,
          _rawItem: entry.raw, // Preserve raw item reference
        }));
    }

    if (fiberTab === 'LEASED_FIBER') {
      return normalizedData
        .filter((entry) => entry.normalizedTenancy === 'leased')
        .map((entry) => ({
          ...entry.formatted,
          _rawItem: entry.raw, // Preserve raw item reference
        }));
    }

    if (fiberTab === 'ROW') {
      const now = new Date();

      const filtered = normalizedData
        .filter((entry) => entry.normalizedTenancy === 'row')
        .filter((entry) => {
          const endDate = entry.raw.lease_end_date ? new Date(entry.raw.lease_end_date) : null;
          if (!endDate) {
            return false;
          }

          const diffInDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

          switch (expiryFilter) {
            case 'EXPIRED':
              return endDate < now;
            case 'EXPIRED_SOON_30':
              return diffInDays >= 0 && diffInDays <= 30;
            case 'EXPIRED_SOON_60':
              return diffInDays >= 0 && diffInDays <= 60;
            case 'EXPIRED_SOON_90':
              return diffInDays >= 0 && diffInDays <= 90;
            case 'EXPIRING_6_MONTHS':
              return diffInDays >= 0 && diffInDays <= 180;
            case 'ALL':
            default:
              return true;
          }
        });

      return filtered.map((entry) => ({
        source_point: entry.raw.source_site_code ?? '--',
        sync_point: entry.raw.sink_site_code ?? '--',
        commssion_date: formatDateValue(entry.raw.commission_date ?? entry.raw.commissioned_at),
        length: formatLengthValue(entry.raw.length),
        lease_start_date: formatDateValue(entry.raw.lease_start_date),
        lease_end_date: formatDateValue(entry.raw.lease_end_date),
        lease_end_date_raw: entry.raw.lease_end_date ?? null,
        concern_dept: entry.raw.concern_department ?? 'N/A',
        poc: entry.raw.poc ?? 'N/A',
        poc_phone: entry.raw.poc_phone ?? 'N/A',
      }));
    }

    return normalizedData.map((entry) => ({
      ...entry.formatted,
      _rawItem: entry.raw, // Preserve raw item reference
    }));
  }, [data, fiberTab, expiryFilter]);

  const columns = useMemo(() => {
    const defaultCell = ({ cell }) => formatCellValue(cell.getValue());

    if (fiberTab === 'ROW') {
      const rowColumns = [
        { accessorKey: 'source_point', header: 'Source Site', size: 140, Cell: defaultCell },
        { accessorKey: 'sync_point', header: 'Sink Site', size: 140, Cell: defaultCell },
        { accessorKey: 'commssion_date', header: 'Commission Date', size: 150, Cell: defaultCell },
        { accessorKey: 'length', header: 'Length', size: 140, Cell: defaultCell },
        {
          accessorKey: 'lease_start_date',
          header: 'Lease Start Date',
          size: 150,
          Cell: defaultCell,
        },
        { accessorKey: 'lease_end_date', header: 'Lease End Date', size: 150, Cell: defaultCell },
        {
          accessorKey: 'concern_dept',
          header: 'Concern Department',
          size: 160,
          Cell: ({ cell }) => {
            const value = cell.getValue();

            const stylesByDept = {
              CDA: {
                background: '#E0F7FA',
                color: '#00796B',
              },
              LDA: {
                background: '#FFF3E0',
                color: '#E65100',
              },
              TDA: {
                background: '#E8F5E9',
                color: '#388E3C',
              },
              MDA: {
                background: '#F3E5F5',
                color: '#6A1B9A',
              },
            };

            const defaultStyle = {
              background: '#FBEAEA',
              color: '#D32F2F',
            };

            const style = stylesByDept[value] || defaultStyle;

            return (
              <span
                style={{
                  background: style.background,
                  color: style.color,
                  padding: '5px 15px',
                  fontSize: '10.5px',
                  fontWeight: 550,
                  borderRadius: '24px',
                  textTransform: 'uppercase',
                }}
              >
                {value || 'N/A'}
              </span>
            );
          },
        },
        { accessorKey: 'poc', header: 'POC', size: 140, Cell: defaultCell },
        { accessorKey: 'poc_phone', header: 'POC Ph#', size: 150, Cell: defaultCell },
      ];

      return rowColumns.map((column) => ({
        ...column,
        muiTableHeadCellProps: {
          ...(column.muiTableHeadCellProps ?? {}),
          align: 'center',
          sx: {
            ...(column.muiTableHeadCellProps?.sx ?? {}),
            backgroundColor: '#E8E9E9',
            textAlign: 'center',
          },
        },
        muiTableBodyCellProps: {
          ...(column.muiTableBodyCellProps ?? {}),
          align: 'center',
          sx: {
            ...(column.muiTableBodyCellProps?.sx ?? {}),
            textAlign: 'center',
          },
        },
      }));
    }

    const baseColumns = [
      { accessorKey: 'layer_name', header: 'Layer Name', size: 160, Cell: defaultCell },
      { accessorKey: 'fiber_type', header: 'Fiber Type', size: 120, Cell: defaultCell },
      { accessorKey: 'region', header: 'Region', size: 120, Cell: defaultCell },
      { accessorKey: 'city', header: 'City', size: 120, Cell: defaultCell },
      { accessorKey: 'ring_unique_name', header: 'Ring', size: 160, Cell: defaultCell },
      {
        accessorKey: 'fiber_tenancy_type',
        header: 'Tenancy Type',
        size: 140,
        Cell: defaultCell,
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const displayValue = formatCellValue(value);

          const stylesByVendor = {
            JAZZ: {
              background: '#FEE2E2',
              color: '#B91C1C',
            },
            WATEEN: {
              background: '#DBEAFE',
              color: '#1D4ED8',
            },
            WTL: {
              background: '#FFF7ED',
              color: '#C2410C',
            },
            NAYATEL: {
              background: '#FEF3C7',
              color: '#B45309',
            },
            MPPL: {
              background: '#EDE9FE',
              color: '#6D28D9',
            },
            PTCL: {
              background: '#DCFCE7',
              color: '#047857',
            },
          };

          const defaultStyle = {
            background: '#F3F4F6',
            color: '#111827',
          };

          const vendorKey = String(value ?? '')
            .trim()
            .toUpperCase();
          const style = stylesByVendor[vendorKey] || defaultStyle;

          return (
            <span
              style={{
                background: style.background,
                color: style.color,
                padding: '3px 10px',
                fontSize: '10.5px',
                fontWeight: 550,
                borderRadius: '24px',
                textTransform: 'capitalize',
              }}
            >
              {displayValue}
            </span>
          );
        },
      },
      {
        accessorKey: 'fiber_pair',
        header: 'Fiber Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'used_pair',
        header: 'Used Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'available_pair',
        header: 'Available Pair',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'link_rate_gbps',
        header: 'Link Rate (Gbps)',
        size: 100,
        Cell: defaultCell,
      },
      {
        accessorKey: 'length',
        header: 'Length',
        size: 110,
        Cell: ({ cell }) => formatLengthValue(cell.getValue()),
      },
      {
        accessorKey: 'commission_date',
        header: 'Commission Date',
        size: 150,
        Cell: defaultCell,
      },
      {
        accessorKey: 'scope_year',
        header: 'Scope Year',
        size: 120,
        Cell: defaultCell,
      },
      {
        accessorKey: 'source_site_code',
        header: 'Source Site',
        size: 130,
        Cell: defaultCell,
      },
      {
        accessorKey: 'sink_site_code',
        header: 'Sink Site',
        size: 130,
        Cell: defaultCell,
      },
      {
        accessorKey: 'link_type',
        header: 'Link Type',
        size: 140,
        Cell: defaultCell,
      },
      ...(fiberTab !== 'ROW'
        ? [
            {
              id: 'actions',
              header: 'Action',
              size: 100,
              enableColumnFilter: false,
              enableSorting: false,
              Cell: ({ row }) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const rawItem = row.original._rawItem;
                      if (rawItem) {
                        setSelectedRecord({ raw: rawItem });
                        setIsModalOpen(true);
                      }
                    }}
                    sx={{
                      color: '#BD1D23',
                      borderColor: '#BD1D23',
                      fontSize: '12px',
                      padding: '1px 12px',
                      minWidth: 'auto',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#BD1D23',
                        backgroundColor: 'rgba(189, 29, 35, 0.08)',
                      },
                    }}
                  >
                    Edit
                  </Button>
                </Box>
              ),
            },
          ]
        : []),
    ];

    return baseColumns.map((column) => ({
      ...column,
      muiTableHeadCellProps: {
        ...(column.muiTableHeadCellProps ?? {}),
        align: 'center',
        sx: {
          ...(column.muiTableHeadCellProps?.sx ?? {}),
          backgroundColor: '#E8E9E9',
          textAlign: 'center',
        },
      },
      muiTableBodyCellProps: {
        ...(column.muiTableBodyCellProps ?? {}),
        align: 'center',
        sx: {
          ...(column.muiTableBodyCellProps?.sx ?? {}),
          textAlign: 'center',
        },
      },
    }));
  }, [fiberTab]);

  const table = useMaterialReactTable({
    columns,
    data: cleanedData,
    columnOrder: columns.map((col) => col.accessorKey),
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowSelection: false,
    enableColumnPinning: true,
    enableFacetedValues: true,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '36px',
        textAlign: 'center',
      },
    },
    muiTableHeadCellProps: {
      align: 'center',
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        textAlign: 'center',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {/* Dropdown */}
          {fiberTab === 'ROW' && (
            <FormControl
              size="small"
              sx={{
                minWidth: '120px',
                maxWidth: '100%',
                backgroundColor: 'white',
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.23)',
                  },
                },
              }}
            >
              <InputLabel
                id="expiry-filter-label"
                sx={{
                  color: '#000',
                  '&.Mui-focused': {
                    color: '#000',
                  },
                }}
              >
                Filter
              </InputLabel>
              <Select
                labelId="expiry-filter-label"
                value={expiryFilter}
                label="Filter"
                onChange={(e) => setExpiryFilter(e.target.value)}
                sx={{
                  color: '#000',
                  '&.Mui-focused': {
                    color: '#000',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#aaa',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                  },
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>

                <ListSubheader disableSticky sx={{ pl: 0, fontSize: '14px', color: '#555' }}>
                  Expiring Soon
                </ListSubheader>
                <MenuItem value="EXPIRED_SOON_30" sx={{ pl: 4 }}>
                  30 Days
                </MenuItem>
                <MenuItem value="EXPIRED_SOON_60" sx={{ pl: 4 }}>
                  60 Days
                </MenuItem>
                <MenuItem value="EXPIRED_SOON_90" sx={{ pl: 4 }}>
                  90 Days
                </MenuItem>

                <MenuItem value="EXPIRING_6_MONTHS">Expiring in 6 Months</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Download Button */}
          <Tooltip title="Download">
            <svg
              style={{ cursor: 'pointer' }}
              onClick={downloadFile}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 10L12 15L17 10"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12 15V3"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Tooltip>
        </Box>
      </>
    ),

    muiTableBodyRowProps: ({ row }) => {
      const rawDateStr = row?.original?.lease_end_date_raw;

      if (!rawDateStr) {
        return {};
      }

      const leaseEndDate = new Date(rawDateStr);
      const now = new Date();
      const isExpired = leaseEndDate.getTime() < now.getTime();

      return isExpired
        ? {
            sx: {
              '& .MuiTableCell-root': {
                backgroundColor: '#FCA5A5 !important',
                color: '#111827 !important',
              },
            },
          }
        : {};
    },
  });

  const downloadFile = () => {
    downloadExcelSheetFunction(data, 'inventory');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleUpdateSuccess = (updatedRecord, recordId) => {
    // Call the parent's update callback if provided
    if (onUpdateRecord) {
      onUpdateRecord(updatedRecord, recordId);
    }
  };

  return (
    <Box sx={{ marginTop: '70px', width: '100%', height: '100%' }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ padding: '8px 16px', backgroundColor: '#fff', marginRight: '16px' }}>
          {/* TABS SECTION ABOVE TABLE */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              borderBottom: '1px solid #ccc',
              width: '100%',
              backgroundColor: '#fff',
            }}
          >
            {['All', 'My Fiber', 'Leased Fiber', 'RoW'].map((label) => {
              const key = label.toUpperCase().replace(' ', '_');
              const isSelected = fiberTab === key;

              return (
                <Box
                  key={key}
                  onClick={() => setFiberTab(key)}
                  sx={{
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderBottom: isSelected ? '2px solid #BD1D23' : '2px solid transparent',
                    fontWeight: isSelected ? 700 : 400,
                    fontSize: isSelected ? '15px' : '14px',
                    color: '#000',
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>

          <div className="box" style={{ marginTop: '0px' }}>
            <MaterialReactTable table={table} />
          </div>
        </Box>
      </Box>
      <EditInventoryModal
        open={isModalOpen}
        onClose={handleModalClose}
        selectedRecord={selectedRecord}
        onUpdateSuccess={handleUpdateSuccess}
        network={network}
      />
    </Box>
  );
}
